/**
 * @file RetryOverlay.ts
 * @role Blocking recoverable-error dialog with one explicit Retry or Dismiss action.
 * @owns Error presentation, action callback, responsive layout, and complete reset.
 * @reads LayoutContext, validated UI tokens, and injected textures.
 * @neverDoes Retry work itself. Load assets. Decide game flow.
 * @invariants 8, 12, 14, 15.
 */
import { Container, Graphics, Text } from 'pixi.js';
import type { IResponsiveElement, LayoutContext } from '@core/layout/LayoutContext';
import type { ModalLayoutConfig, UiThemeConfig } from '@core/config/UiConfig';
import type { TextureResolver } from '@core/assets/TextureResolver';
import { ModalShell } from './ModalShell';
import { PopupButton } from './PopupButton';

export class RetryOverlay extends Container implements IResponsiveElement {
  private readonly shell: ModalShell;
  private readonly icon = new Graphics();
  private readonly iconLabel: Text;
  private readonly message: Text;
  private readonly action: PopupButton;
  private onAction: (() => void) | undefined;
  private visibilityHandler: ((visible: boolean) => void) | undefined;
  private opened = false;

  constructor(
    private readonly modalConfig: ModalLayoutConfig,
    private readonly theme: UiThemeConfig,
    resolveTexture: TextureResolver = () => undefined,
    reducedMotion = false,
  ) {
    super();
    this.visible = false;
    this.shell = new ModalShell({
      title: 'CONNECTION ISSUE',
      layout: modalConfig,
      theme,
      textures: {
        close: resolveTexture('icon_close'),
        roundButtonBackground: resolveTexture('ui_button_round_background'),
      },
      dismissible: false,
      reducedMotion,
    });
    this.iconLabel = new Text({
      text: '!',
      style: { fill: 0x0a0a12, fontSize: 28, fontWeight: '900' },
    });
    this.iconLabel.anchor.set(0.5);
    this.message = new Text({
      text: '',
      style: {
        fill: theme.textPrimary,
        fontSize: modalConfig.bodyFontSize,
        lineHeight: 24,
        align: 'center',
        wordWrap: true,
        wordWrapWidth: 420,
      },
    });
    this.message.anchor.set(0.5, 0);
    this.action = new PopupButton({
      label: 'RETRY',
      width: 220,
      height: 52,
      variant: 'primary',
      accessibleTitle: 'Retry',
      tabIndex: 1,
      theme,
    }).onTap(() => this.onAction?.());
    this.shell.content.addChild(this.icon, this.iconLabel, this.message, this.action);
    this.addChild(this.shell);
    this.action.setAccessibilityEnabled(false);
  }

  show(message: string, onAction: () => void, buttonLabel = 'Retry'): void {
    this.shell.setTitle(buttonLabel === 'Dismiss' ? 'SPIN INTERRUPTED' : 'CONNECTION ISSUE');
    this.message.text = message;
    this.action.setLabel(buttonLabel.toUpperCase());
    this.onAction = onAction;
    this.opened = true;
    this.visible = true;
    this.shell.show();
    this.action.setAccessibilityEnabled(true);
    this.visibilityHandler?.(true);
  }

  hide(animate = false): void {
    this.opened = false;
    this.onAction = undefined;
    this.action.reset();
    this.action.setAccessibilityEnabled(false);
    this.visibilityHandler?.(false);
    this.shell.hide(animate, () => {
      this.visible = false;
    });
  }

  onVisibilityChange(handler: (visible: boolean) => void): void {
    this.visibilityHandler = handler;
  }

  onActivated(handler: () => void): this {
    this.action.onActivated(handler);
    return this;
  }

  get isOpen(): boolean {
    return this.opened;
  }

  applyLayout(layout: LayoutContext): void {
    const bounds = this.shell.layout(layout, this.modalConfig.errorWidth, 300, 'center');
    const iconY = 34;
    this.icon
      .clear()
      .circle(bounds.width / 2, iconY, 23)
      .fill({ color: this.theme.danger })
      .circle(bounds.width / 2, iconY, 23)
      .stroke({ color: 0xffb0aa, width: 2 });
    this.iconLabel.position.set(bounds.width / 2, iconY);
    this.message.style.wordWrapWidth = Math.max(160, bounds.width - 24);
    this.message.position.set(bounds.width / 2, 74);
    this.action.layout(Math.min(220, bounds.width), 52);
    this.action.position.set(bounds.width / 2, Math.max(154, bounds.height - 30));
  }
}
